import React, { useCallback, useEffect, useState } from "react";
import { Select } from "antd";
import { getUsers } from "../../../../apis/user";
import { saveVisit, updateVisit } from "../../../../apis/visit";
import { useNotification } from "../../../../NotificationContext";
import { format } from "date-fns";
import { getRooms } from "../../../../apis/room";
import MultiSelect from "../../../../components/MultiSelect";
import { getDisorders, saveDisorder } from "../../../../apis/disorder";
import { Option } from "../../../../interfaces/MultiSelectTypes";

const { Option: SelectOption } = Select;

interface IPDAdmissionFormProps {
  patientId: number;
  clinicVisitTypeId: number;
  initialData?: {
    admittingDoctorId?: number | null;
    chiefComplaint?: string;
    provisionalDiagnosis?: any[];
    roomId?: number | null;
    paymentType?: "self" | "insurance" | "corporate";
    admissionDate?: string;
    admissionTime?: string;
  };
  visitId?: number;
  onSave: (visit: any) => void;
  onCancel: () => void;
}

const IPDAdmissionForm: React.FC<IPDAdmissionFormProps> = ({
  patientId,
  clinicVisitTypeId,
  initialData,
  visitId,
  onSave,
  onCancel,
}) => {
  const { showNotification } = useNotification();
  const [doctors, setDoctors] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDiagnosisDropdownOpen, setIsDiagnosisDropdownOpen] = useState(false);
  const [diagnosisSearchTerm, setDiagnosisSearchTerm] = useState("");
  const [disorders, setDisorders] = useState<Array<Option>>([]);

  // Generate time slots in 15-minute intervals in AM/PM format
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time24 = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        const time12 = convertTo12Hour(time24);
        slots.push({ value: time24, label: time12 });
      }
    }
    return slots;
  };

  // Convert 24-hour format (HH:mm) to 12-hour AM/PM format
  const convertTo12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
  };


  // Round time to nearest 15-minute slot
  const roundToNearest15Minutes = (time: string): string => {
    const [hours, minutes] = time.split(":").map(Number);
    const roundedMinutes = Math.round(minutes / 15) * 15;
    if (roundedMinutes >= 60) {
      return `${(hours + 1).toString().padStart(2, "0")}:00`;
    }
    return `${hours.toString().padStart(2, "0")}:${roundedMinutes.toString().padStart(2, "0")}`;
  };

  const timeSlots = generateTimeSlots();
  const currentTime = format(new Date(), "HH:mm");
  const defaultTime = roundToNearest15Minutes(currentTime);

  const buildFormData = useCallback((data?: IPDAdmissionFormProps["initialData"]) => ({
    admittingDoctorId: data?.admittingDoctorId || (null as number | null),
    chiefComplaint: data?.chiefComplaint || "",
    provisionalDiagnosis: (data?.provisionalDiagnosis || []).map((d: any) => ({
      label:
        d.label ||
        d.name ||
        d.disorder?.name ||
        d.disorder_name ||
        d.disorderName ||
        String(d.value || d.id || d.disorder_id || d.disorderId || ""),
      value: String(d.value || d.id || d.disorder_id || d.disorderId || ""),
    })) as Option[],
    roomId: data?.roomId || (null as number | null),
    paymentType: data?.paymentType || ("self" as "self" | "insurance" | "corporate"),
    admissionDate: data?.admissionDate || format(new Date(), "yyyy-MM-dd"),
    admissionTime: data?.admissionTime || defaultTime, // Store in 24-hour format internally
  }), [defaultTime]);

  const [formData, setFormData] = useState(buildFormData(initialData));

  useEffect(() => {
    setFormData(buildFormData(initialData));
  }, [buildFormData, initialData]);

  useEffect(() => {
    fetchDoctors();
    fetchRooms();
    fetchDisorders();
  }, []);

  const fetchDoctors = async () => {
    try {
      const res: any = await getUsers({ role: "doctor", per_page: 1000 });
      setDoctors(res.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch doctors:", error);
    }
  };

  const fetchRooms = async () => {
    try {
      const res: any = await getRooms({ per_page: 1000 , status: "available"});
      console.log({res});
      setRooms(res.data || []);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    }
  };

  const fetchDisorders = async (search: string = "") => {
    try {
      const {
        data: { data: response },
      } = await getDisorders({ name: search, type: "disease" });
      console.log({response});
      
      const disorderData = response.map((i: any) => {
        return {
          label: i.name,
          value: String(i.id),
        };
      });
      setDisorders(disorderData);
    } catch (error) {
      console.error("Failed to fetch disorders:", error);
    }
  };

  const handleDiagnosisSearch = (search: string) => {
    setDiagnosisSearchTerm(search);
    fetchDisorders(search);
  };

  const handleDiagnosisSelect = (option: Option) => {
    if (
      !formData.provisionalDiagnosis.some(
        (selected) => selected.value === option.value
      )
    ) {
      setFormData({
        ...formData,
        provisionalDiagnosis: [...formData.provisionalDiagnosis, option],
      });
    }
  };

  const handleDiagnosisRemove = (option: Option) => {
    const updatedDiagnosis = formData.provisionalDiagnosis.filter(
      (selected) => selected.value !== option.value
    );
    setFormData({
      ...formData,
      provisionalDiagnosis: updatedDiagnosis,
    });
  };

  const createNewDisorder = async (disorderName: string) => {
    try {
      const { data } = await saveDisorder({ name: disorderName, type: "disease" });
      setFormData({
        ...formData,
        provisionalDiagnosis: [
          ...formData.provisionalDiagnosis,
          {
            label: data.data.name,
            value: String(data.data.id),
          },
        ],
      });
      setDiagnosisSearchTerm("");
      fetchDisorders();
    } catch (error) {
      console.error("Failed to create disorder:", error);
    }
  };

  const handleSave = async () => {
    const validationErrors: any = {};

    if (!formData.admittingDoctorId) {
      validationErrors.admittingDoctorId = "Admitting Doctor is required";
    }
    if (!formData.chiefComplaint.trim()) {
      validationErrors.chiefComplaint = "Chief Complaint is required";
    }
    if (!formData.roomId) {
      validationErrors.roomId = "Room is required";
    }
    if (!formData.admissionDate) {
      validationErrors.admissionDate = "Admission Date is required";
    }
    if (!formData.admissionTime) {
      validationErrors.admissionTime = "Admission Time is required";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSaving(true);

    try {
      // Combine date and time
      const admissionDateTime = new Date(
        `${formData.admissionDate}T${formData.admissionTime}`
      ).toISOString();

      // Filter out invalid values and convert to objects with disorder_id
      const validDiagnosis = formData.provisionalDiagnosis
        .filter((d: Option) => d && d.value && !isNaN(Number(d.value)))
        .map((d: Option) => ({ disorder_id: Number(d.value) }));

      const visitPayload = {
        patientId,
        clinicVisitTypeId,
        userId: formData.admittingDoctorId,
        chiefComplaint: formData.chiefComplaint,
        provisionalDiagnosis: validDiagnosis,
        date: admissionDateTime,
        isInpatient: true,
        is_scheduled: false,
        meta: {
          roomId: formData.roomId,
          paymentType: formData.paymentType,
          admissionDate: formData.admissionDate,
          admissionTime: formData.admissionTime,
        },
      };

      let savedVisit: any;
      
      if (visitId) {
        // Update existing visit
        savedVisit = await updateVisit({
          id: visitId,
          ...visitPayload,
        });
        showNotification(
          "success",
          "Admission updated successfully",
          "",
          "topRight"
        );
      } else {
        // Create new visit
        savedVisit = await saveVisit(visitPayload);
        // Admission entry creation is handled by backend, not called from frontend
        // The backend should automatically create the admission entry when the visit is created
        showNotification(
          "success",
          "Patient admitted successfully",
          "",
          "topRight"
        );
      }
      
      onSave(savedVisit);
    } catch (error: any) {
      console.error("Failed to save admission:", error);
      const message =
        error?.response?.data?.message || "Failed to save admission";
      showNotification("error", message, "", "topRight");
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Admitting Doctor <span className="text-red-500">*</span>
        </label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select Admitting Doctor"
          value={formData.admittingDoctorId}
          onChange={(value) =>
            setFormData({ ...formData, admittingDoctorId: value })
          }
        >
          {doctors.map((doctor) => (
           <SelectOption key={doctor.id} value={doctor.id}>
              {doctor.firstName} {doctor.lastName}
            </SelectOption>
          ))}
        </Select>
        {errors.admittingDoctorId && (
          <span className="text-sm text-red-500">{errors.admittingDoctorId}</span>
        )}
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Chief Complaint <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.chiefComplaint}
          onChange={(e) =>
            setFormData({ ...formData, chiefComplaint: e.target.value })
          }
          rows={3}
          className="px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Enter chief complaint..."
        />
        {errors.chiefComplaint && (
          <span className="text-sm text-red-500">{errors.chiefComplaint}</span>
        )}
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Provisional Diagnosis
        </label>
        <MultiSelect
          onAddNewOption={createNewDisorder}
          selectedOptions={formData.provisionalDiagnosis}
          options={disorders}
          isOpen={isDiagnosisDropdownOpen}
          searchTerm={diagnosisSearchTerm}
          onSearch={handleDiagnosisSearch}
          onOptionSelect={handleDiagnosisSelect}
          onOptionRemove={handleDiagnosisRemove}
          toggleDropdown={() => setIsDiagnosisDropdownOpen(!isDiagnosisDropdownOpen)}
          placeholder="Select or search disorders..."
        />
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Room <span className="text-red-500">*</span>
        </label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select Room"
          value={formData.roomId}
          onChange={(value) => setFormData({ ...formData, roomId: value })}
        >
          {rooms.map((room) => (
            <SelectOption key={room.id} value={room.id}>
              {room.name} ({room.roomType?.name})

            </SelectOption>
          ))}
        </Select>
        {errors.roomId && (
          <span className="text-sm text-red-500">{errors.roomId}</span>
        )}
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Payment Type <span className="text-red-500">*</span>
        </label>
        <Select
          style={{ width: "100%" }}
          value={formData.paymentType}
          onChange={(value: "self" | "insurance" | "corporate") =>
            setFormData({ ...formData, paymentType: value })
          }
        >
          <SelectOption value="self">Self</SelectOption>
          <SelectOption value="insurance">Insurance</SelectOption>
          <SelectOption value="corporate">Corporate</SelectOption>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Admission Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.admissionDate}
            onChange={(e) =>
              setFormData({ ...formData, admissionDate: e.target.value })
            }
            className="px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
          {errors.admissionDate && (
            <span className="text-sm text-red-500">{errors.admissionDate}</span>
          )}
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Admission Time <span className="text-red-500">*</span>
          </label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Admission Time"
            value={formData.admissionTime}
            onChange={(value) =>
              setFormData({ ...formData, admissionTime: value })
            }
            showSearch
            filterOption={(input, option) => {
              const children = String(option?.children || "");
              return children.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {timeSlots.map((slot) => (
              <SelectOption key={slot.value} value={slot.value}>
                {slot.label}
              </SelectOption>
            ))}
          </Select>
          {errors.admissionTime && (
            <span className="text-sm text-red-500">{errors.admissionTime}</span>
          )}
        </div>
      </div>

      <div className="flex gap-3 justify-end pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving..." : visitId ? "Update Admission" : "Admit Patient"}
        </button>
      </div>
    </div>
  );
};

export default IPDAdmissionForm;

